/**
 * @file Currency input component with decimal support and reverse number input.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { formatNumber } from '../../util/format-money';
import InputGroup from './InputGroup';
import { CURRENCY, CURRENCY_SYMBOLS } from '../../constants/currency';

class CurrencyInput extends Component {
  static propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    wrapperClassName: PropTypes.string,
    delimiter: PropTypes.string,
    onChange: PropTypes.func,
    precision: PropTypes.number,
    decimal: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.node,
    labelNote: PropTypes.string,
    labelTip: PropTypes.string,
    labelType: PropTypes.oneOf(['standard', 'expanded']),
    labelSrOnly: PropTypes.bool,
    inputRef: PropTypes.func,
    note: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    placeholder: PropTypes.string,
    currency: PropTypes.oneOf(Object.keys(CURRENCY)),
  };

  static defaultProps = {
    value: 0,
    precision: 2,
    className: '',
    decimal: '.',
    delimiter: ',',
    onChange: () => {},
  };

  state = {
    rawValue: this.props.value,
  };

  componentDidMount() {
    this.notifyParentWithRawValue(this.state.rawValue);
  }

  componentDidUpdate() {
    if (this.props.value !== this.state.rawValue) {
      this.setState(() => ({ rawValue: this.props.value }));
    }
  }

  onInputChange = event => {
    const { value } = event.target;
    const rawValue = this.getRawValue(value) || '';

    this.notifyParentWithRawValue(rawValue);
    this.setState(() => ({ rawValue }));
  };

  getRawValue = displayedValue => {
    const { delimiter, decimal } = this.props;
    const result = displayedValue.replace(delimiter, '').replace(decimal, '');

    return parseInt(result, 10);
  };

  formattedRawValue = rawValue => {
    if (!rawValue) {
      return '';
    }

    const { precision, delimiter, decimal } = this.props;
    const minChars = '0'.length + precision;
    const result = `${rawValue}`.padStart(minChars, '0');
    const beforeDecimal = result.slice(0, result.length - precision);
    const afterDecimal = result.slice(result.length - precision);

    return formatNumber(beforeDecimal, delimiter) + decimal + afterDecimal;
  };

  notifyParentWithRawValue(rawValue) {
    const display = this.formattedRawValue(rawValue);
    this.props.onChange(rawValue, display);
  }

  render() {
    const { currency } = this.props;
    return (
      <InputGroup
        {...this.props}
        id={this.props.id}
        label={this.props.label}
        labelNote={this.props.labelNote}
        labelTip={this.props.labelTip}
        labelSrOnly={this.props.labelSrOnly}
        labelType={this.props.labelType}
        className={this.props.className}
        inputClassName={this.props.inputClassName}
        wrapperClassName={this.props.wrapperClassName}
        inputRef={this.props.inputRef}
        note={this.props.note}
        onChange={this.onInputChange}
        addon={CURRENCY_SYMBOLS[currency ?? 'USD']}
        type="text"
        value={this.formattedRawValue(this.state.rawValue)}
      />
    );
  }
}

export default CurrencyInput;
